import { PageHeader } from "@ant-design/pro-components";
import { CopyAllOutlined } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Radio,
  Space,
  Spin,
  Steps,
  Tag,
  message,
} from "antd";
import { toJalaali } from "jalaali-js";
import { memo, useState } from "react";
import api from "../../../api/api";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";
import styles from "./CopyRoadmap.module.css";

const ERROR_MESSAGES = {
  INVALID_SOURCE: "شماره مبدا نامعتبر است",
  INVALID_DESTINATION: "شماره مقصد نامعتبر است",
  INVALID_ROADMAP: "نقشه انتخاب‌شده نامعتبر است",
  NO_QUESTIONNAIRE: "برای نقشه انتخاب‌شده پرسشنامه‌ای ثبت نشده است",
  INVALID_PRODUCT: "محصول نقشه انتخاب‌شده نامعتبر است",
  PRODUCT_ALREADY_OWNED: "کاربر مقصد این محصول را قبلاً دارد و امکان کپی نیست",
  RENEWAL_WINDOW_NOT_PASSED:
    "هنوز ۹۰ روز از اولین نقشه راه کاربر مقصد نگذشته است",
  PREVIOUS_NOT_ANSWERED:
    "کاربر مقصد هنوز پرسشنامه نقشه راه قبلی خود را تکمیل نکرده است",
};

const resolveError = (error) => {
  const code = error?.response?.data?.error?.message;
  return ERROR_MESSAGES[code] || code || "خطای ناشناخته";
};

const formatJalali = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  const j = toJalaali(d.getFullYear(), d.getMonth() + 1, d.getDate());
  return `${j.jy}-${String(j.jm).padStart(2, "0")}-${String(j.jd).padStart(2, "0")}`;
};

const CopyRoadmap = () => {
  const [step, setStep] = useState(0);
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmapId, setSelectedRoadmapId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState({ message: "", type: "" });

  const loadRoadmaps = () => {
    const src = ConvertNumbersToEn((source || "").trim());
    const dest = ConvertNumbersToEn((destination || "").trim());

    if (!src) {
      message.warning("شماره مبدا را وارد کنید");
      return;
    }
    if (!dest) {
      message.warning("شماره مقصد را وارد کنید");
      return;
    }

    setSource(src);
    setDestination(dest);
    setResult({ message: "", type: "" });
    setLoadingList(true);

    api
      .get("/roadmaps/copy-source-list", { params: { mobile: src } })
      .then((res) => {
        const list = res.data?.roadmaps || [];
        if (list.length === 0) {
          message.info("نقشه قابل کپی برای این شماره یافت نشد");
          setRoadmaps([]);
          return;
        }
        setRoadmaps(list);
        setSelectedRoadmapId(list[0].roadmapId);
        setStep(1);
      })
      .catch((error) => {
        const messageText = resolveError(error);
        message.error(messageText);
        setResult({ message: messageText, type: "error" });
      })
      .finally(() => setLoadingList(false));
  };

  const submitCopy = () => {
    if (!selectedRoadmapId) {
      message.warning("یک نقشه را انتخاب کنید");
      return;
    }

    setSubmitting(true);
    setResult({ message: "", type: "" });

    api
      .post("/roadmaps/copytodummy", {
        source,
        destination,
        sourceRoadmapId: selectedRoadmapId,
      })
      .then(() => {
        message.success("نقشه راه با موفقیت کپی شد");
        setResult({ message: "نقشه راه با موفقیت کپی شد", type: "success" });
        setStep(2);
      })
      .catch((error) => {
        const messageText = resolveError(error);
        message.error(messageText);
        setResult({ message: messageText, type: "error" });
      })
      .finally(() => setSubmitting(false));
  };

  const resetAll = () => {
    setStep(0);
    setSource("");
    setDestination("");
    setRoadmaps([]);
    setSelectedRoadmapId(null);
    setResult({ message: "", type: "" });
  };

  return (
    <div className={styles.CopyRoadmap}>
      <PageHeader
        title="کپی نقشه راه"
        avatar={{ icon: <CopyAllOutlined />, size: "large" }}
      />

      <Card className={styles.Form}>
        <Steps
          size="small"
          current={step}
          items={[
            { title: "شماره‌ها" },
            { title: "انتخاب نقشه" },
            { title: "نتیجه" },
          ]}
          style={{ marginBottom: "1.5rem" }}
        />

        {step === 0 && (
          <Form layout="vertical" autoComplete="off">
            <Form.Item label="موبایل مبدا" required>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                onPressEnter={loadRoadmaps}
              />
            </Form.Item>

            <Form.Item label="موبایل مقصد" required>
              <Input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                onPressEnter={loadRoadmaps}
              />
            </Form.Item>

            <Button type="primary" loading={loadingList} onClick={loadRoadmaps}>
              بارگذاری نقشه‌ها
            </Button>
          </Form>
        )}

        {step === 1 && (
          <Spin spinning={loadingList}>
            {roadmaps.length === 0 ? (
              <Empty description="نقشه‌ای یافت نشد" />
            ) : (
              <Radio.Group
                value={selectedRoadmapId}
                onChange={(e) => setSelectedRoadmapId(e.target.value)}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {roadmaps.map((r) => (
                    <Radio
                      key={r.roadmapId}
                      value={r.roadmapId}
                      style={{
                        width: "100%",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: "0.75rem",
                      }}
                    >
                      <div>
                        <strong>{r.productTitle}</strong>
                        <div style={{ marginTop: 4 }}>
                          <Tag>{r.status || "-"}</Tag>
                          <Tag color="blue">{r.productCode}</Tag>
                          <span dir="ltr">{formatJalali(r.createdAt)}</span>
                        </div>
                      </div>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}

            <Space style={{ marginTop: "1.5rem" }}>
              <Button onClick={() => setStep(0)}>بازگشت</Button>
              <Button
                type="primary"
                loading={submitting}
                disabled={roadmaps.length === 0}
                onClick={submitCopy}
              >
                کپی کردن
              </Button>
            </Space>
          </Spin>
        )}

        {step === 2 && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              message="نقشه راه با موفقیت کپی شد"
              type="success"
              showIcon
            />
            <Button type="primary" onClick={resetAll}>
              کپی جدید
            </Button>
          </Space>
        )}

        {result.message && step !== 2 && (
          <Alert
            style={{ marginTop: "1rem" }}
            message={result.message}
            type={result.type}
          />
        )}
      </Card>
    </div>
  );
};

export default memo(CopyRoadmap);
